import type { UseMutationResult } from "@tanstack/react-query"
import { useMutation, useQueryClient } from "@tanstack/react-query"

import { queryKeys } from "~/api/cache"
import {
  SOLAR_COMPUTE_INVALID_CODE,
  SOLAR_PRODUCT_OFFERING_SUPERSEDED_CODE,
  solarProposalErrorCode,
} from "~/api/solar-proposal-errors"

import {
  invalidateSolarPriceDependents,
  invalidateSolarPricingInputSteps,
} from "./solar-price-dependents"
import type {
  SolarProposalComputeDto,
  UpdateSolarProposalComputeDto,
} from "../dto/solar-proposal-compute.dto"
import { solarProposalService } from "../services/solar-proposal.service"

export type UpdateSolarProposalComputeVariables = {
  proposalId: string
  body: UpdateSolarProposalComputeDto
}

/**
 * Saves the AI compute node, or none. The answer is the Compute read itself.
 * Pricing and savings then report out of date until refreshed on purpose, so
 * their reads (and those that follow them) refetch; nothing recalculates. A
 * failure refetches the read, since a lock may be why it failed.
 */
export default function useUpdateSolarProposalCompute(): UseMutationResult<
  SolarProposalComputeDto,
  unknown,
  UpdateSolarProposalComputeVariables
> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ proposalId, body }: UpdateSolarProposalComputeVariables) =>
      solarProposalService.putCompute(proposalId, body),
    onSuccess: async (saved, { proposalId }) => {
      queryClient.setQueryData(
        queryKeys.solarProposal.compute(proposalId),
        saved,
      )
      // Pricing and savings first, awaited, before the save settles.
      await Promise.all(
        [
          queryKeys.solarProposal.pricing(proposalId),
          queryKeys.solarProposal.savings(proposalId),
        ].map((queryKey) =>
          queryClient.invalidateQueries({ queryKey, exact: true }),
        ),
      )
      invalidateSolarPriceDependents(queryClient, proposalId, {
        except: [`pricing`, `savings`],
      })
      // The node's PowurOS adders and the Finance Partner & Equipment read
      // (its own save checks the node is still offered).
      invalidateSolarPricingInputSteps(queryClient, proposalId)
      void queryClient.invalidateQueries({
        queryKey: queryKeys.solarProposal.productOffering(proposalId),
        exact: true,
      })
      // The design read's offset counts the node's load.
      void queryClient.invalidateQueries({
        queryKey: queryKeys.solarProposal.design(proposalId),
        exact: true,
      })
    },
    onError: (error, { proposalId }) => {
      // Superseded means the options were for an older proposal or Site;
      // invalid means PowurOS no longer offers the chosen node. Either way the
      // preview is stale, so it refetches with the read.
      const code = solarProposalErrorCode(error)
      const optionsStale =
        code === SOLAR_PRODUCT_OFFERING_SUPERSEDED_CODE ||
        code === SOLAR_COMPUTE_INVALID_CODE
      void queryClient.invalidateQueries({
        queryKey: queryKeys.solarProposal.compute(proposalId),
        ...(optionsStale ? {} : { exact: true }),
      })
      // The partner gate lives on the product-offering read: a save refused
      // because the partner changed then shows the step read-only.
      void queryClient.invalidateQueries({
        queryKey: queryKeys.solarProposal.productOffering(proposalId),
        exact: true,
      })
    },
  })
}
